import {
  TaxRegistrationShareholderType,
  TaxRegistrationTaxCalculationMethod,
  type TaxRegistrationAttachment,
  type TaxRegistrationQuestions,
  type TaxRegistrationRepresentative,
  type TaxRegistrationShareholder,
} from "./types";

/**
 * The two ELSTER rules that a KapG questionnaire fails most often, checked here
 * so the operator sees them next to the field instead of as an ERiC code after
 * a round trip. The rule numbers are quoted in the messages because that is
 * what the ERiC answer names if it gets that far.
 *
 * Source: the FsE KapG rule set (SMT-1294). The Beteiligungssumme has a notice
 * band between 99 and 100 percent, which a rounded three-way split lands in, so
 * that band warns and still lets the questionnaire through.
 */
export const SHARE_TOTAL_NOTICE_FROM = 99;
export const SHARE_TOTAL_EXACT = 100;

export interface KapgGateIssue {
  /**
   * ELSTER rule number, shown to the operator. Absent for a gate of our own,
   * which has no rule number to quote (the missing Gesellschaftsvertrag).
   */
  rule?: string;
  /** Key under `kapg.gates` in the catalogue. */
  messageKey: string;
  severity: "error" | "notice";
}

/**
 * Zeile 67a. A sum of two-decimal percentages is not exact in binary, so it is
 * rounded to the precision the field accepts before it is compared.
 */
export const getShareTotal = (
  shareholders?: TaxRegistrationShareholder[]
): number => {
  const total = (shareholders ?? []).reduce(
    (sum, shareholder) => sum + (Number(shareholder?.percentage) || 0),
    0
  );

  return Math.round(total * 100) / 100;
};

const getShareTotalIssue = (total: number): KapgGateIssue | undefined => {
  if (total < SHARE_TOTAL_NOTICE_FROM) {
    return {
      rule: "180000365",
      messageKey: "shareTotalBelow",
      severity: "error",
    };
  }
  if (total > SHARE_TOTAL_EXACT) {
    return {
      rule: "180000364",
      messageKey: "shareTotalAbove",
      severity: "error",
    };
  }
  if (total < SHARE_TOTAL_EXACT) {
    return {
      rule: "180000582",
      messageKey: "shareTotalNotice",
      severity: "notice",
    };
  }

  return undefined;
};

/**
 * Zeilen 194 to 196. Rule 180000541 is a `KeinFeldAngegeben`: the error is the
 * Ist-Versteuerung *without* a reason, not the reason itself. Both reasons may
 * be given at once. A vermögensverwaltende Gesellschaft has no vat object at
 * all, so nothing to answer.
 */
const getCashAccountingIssue = (
  questions: TaxRegistrationQuestions
): KapgGateIssue | undefined => {
  if (questions.company?.vermoegensverwaltend) return undefined;

  const vat = questions.vat;
  const isCashAccounting =
    vat?.taxCalculationMethod === TaxRegistrationTaxCalculationMethod.CASH;
  const hasReason = Boolean(
    vat?.cashAccountingTurnoverLimit || vat?.cashAccountingSection148Ao
  );

  return isCashAccounting && !hasReason
    ? {
        rule: "180000541",
        messageKey: "cashAccountingReasonRequired",
        severity: "error",
      }
    : undefined;
};

/**
 * Zeile 227. Not an ERiC rule: ELSTER accepts a FsE_KapG with no `Anhaenge`
 * block at all, and the questionnaire then states that the Gesellschaftsvertrag
 * is attached while nothing is. The Finanzamt asks for it by post, weeks later,
 * which is the failure SMT-1341 exists to remove — so the gate is ours, and it
 * blocks rather than warns.
 */
export const getArticlesOfAssociationGateIssue = (
  attachments: TaxRegistrationAttachment[]
): KapgGateIssue | undefined =>
  attachments.length === 0
    ? { messageKey: "articlesOfAssociationMissing", severity: "error" }
    : undefined;

export const getShareTotalGateIssue = (
  questions: TaxRegistrationQuestions
): KapgGateIssue | undefined =>
  getShareTotalIssue(getShareTotal(questions.shareholders));

export const getCashAccountingGateIssue = getCashAccountingIssue;

/** Absent, `null`, or blank after trim: the shape ELSTER itself treats as unanswered. */
const isBlank = (value?: string | null): boolean => !value || !value.trim();

/**
 * Zeilen 68 to 78, rule 180000301. A natural-person Anteilseigner must give
 * firstName, lastName and dateOfBirth together, or ERiC rejects the entry. A
 * LEGAL shareholder carries none of the three fields and is not subject to the
 * rule at all.
 */
const isShareholderPersonIncomplete = (
  shareholder: TaxRegistrationShareholder
): boolean =>
  shareholder.type !== TaxRegistrationShareholderType.LEGAL &&
  (isBlank(shareholder.firstName) ||
    isBlank(shareholder.lastName) ||
    isBlank(shareholder.dateOfBirth));

export const getShareholderPersonGateIssue = (
  questions: TaxRegistrationQuestions
): KapgGateIssue | undefined =>
  (questions.shareholders ?? []).some(isShareholderPersonIncomplete)
    ? {
        rule: "180000301",
        messageKey: "shareholderPersonIncomplete",
        severity: "error",
      }
    : undefined;

/**
 * Zeilen 25 to 30, rule 180000409. The same shape of rule for a gesetzlicher
 * Vertreter: firstName, lastName and dateOfBirth are required together.
 */
const isRepresentativePersonIncomplete = (
  representative: TaxRegistrationRepresentative
): boolean =>
  isBlank(representative.firstName) ||
  isBlank(representative.lastName) ||
  isBlank(representative.dateOfBirth);

export const getRepresentativePersonGateIssue = (
  questions: TaxRegistrationQuestions
): KapgGateIssue | undefined =>
  (questions.representatives ?? []).some(isRepresentativePersonIncomplete)
    ? {
        rule: "180000409",
        messageKey: "representativePersonIncomplete",
        severity: "error",
      }
    : undefined;

/** Rule 180000350: FsE_KapG needs at least one gesetzlicher Vertreter. */
export const getRepresentativeMissingGateIssue = (
  questions: TaxRegistrationQuestions
): KapgGateIssue | undefined =>
  (questions.representatives ?? []).length === 0
    ? {
        rule: "180000350",
        messageKey: "representativeMissing",
        severity: "error",
      }
    : undefined;

/**
 * Thrown when a preview or a transmission is stopped by one of the gates above.
 *
 * A class of its own because the form must not report it: the caller that asked
 * to process knows whether it was a preview, an XML download or a transmission,
 * and shows the message in that context. Without the distinction one gate
 * raised two messages for the same failure.
 */
export class KapgGateError extends Error {
  readonly issues: KapgGateIssue[];

  constructor(message: string, issues: KapgGateIssue[]) {
    super(message);
    this.name = "KapgGateError";
    this.issues = issues;
  }
}

/** Every issue that stops a preview or a transmission. */
export const getBlockingGateIssues = (
  questions: TaxRegistrationQuestions,
  attachments: TaxRegistrationAttachment[]
): KapgGateIssue[] =>
  [
    getShareTotalGateIssue(questions),
    getCashAccountingGateIssue(questions),
    getArticlesOfAssociationGateIssue(attachments),
    getShareholderPersonGateIssue(questions),
    getRepresentativePersonGateIssue(questions),
    getRepresentativeMissingGateIssue(questions),
  ]
    .filter((issue): issue is KapgGateIssue => issue !== undefined)
    .filter(({ severity }) => severity === "error");
